import React, { useState, useEffect, useCallback } from "react";
import { getUsers, deleteUser, resendOnboardingEmail } from "./api/users";
import { getRoles } from "../roles/api/roles";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { UsersDataTable } from "./components/users-data-table";
import CreateUserModal from "./CreateUserModal";
import EditUserModal from "./EditUserModal";
import { Role, User } from "types/settings";

const Users = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);

  const [search, setSearch] = useState<string>("");
  const [selectedUserType, setSelectedUserType] = useState<string | undefined>();
  const [selectedRoleId, setSelectedRoleId] = useState<number | undefined>();

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchUsersData(true);
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [search, selectedUserType, selectedRoleId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const rolesResponse = await getRoles();
        setRoles(rolesResponse || []);
      } catch (err: any) {
        console.error("Error fetching data:", err);
        setError(err?.message || "Failed to fetch data");
      }
    };
    fetchData();
  }, []);

  const fetchUsersData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError("");
      const response = await getUsers(selectedUserType, selectedRoleId, search);
      setUsers(response || []);
    } catch (err: any) {
      console.error("Error fetching users:", err);
      if (showLoading) setError(err?.message || "Failed to fetch users");
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [selectedUserType, selectedRoleId, search]);

  const hasQueuedRows = users.some(u => u.onboarding_email_status === "queued");

  // While any row is Queued, check for updates every 5s for up to 2 minutes (120s max).
  useEffect(() => {
    if (!hasQueuedRows) {
      return;
    }

    const startTime = Date.now();

    const interval = setInterval(() => {
      if (Date.now() - startTime >= 120000) {
        clearInterval(interval);
        return;
      }
      fetchUsersData(false);
    }, 5000);

    return () => clearInterval(interval);
  }, [hasQueuedRows, fetchUsersData]);

  const handleDeleteUser = async (user_id: string) => {
    try {
      await deleteUser(user_id);
      await fetchUsersData(true);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  };

  const handleResendEmail = async (user: User) => {
    try {
      // Optimistically update status to queued
      setUsers(prev =>
        prev.map(u =>
          u.user_id === user.user_id
            ? { ...u, onboarding_email_status: "queued" }
            : u
        )
      );
      await resendOnboardingEmail(user.user_id);
      await fetchUsersData(false);
    } catch (err) {
      console.error("Failed to resend onboarding email:", err);
      await fetchUsersData(false);
    }
  };

  const handleEditUser = (user: User) => {
    setUserToEdit(user);
    setEditModalOpen(true);
  };

  const handleUserUpdated = async () => {
    await fetchUsersData(true);
  };

  const handleUserCreated = async () => {
    await fetchUsersData(true);
  };

  if (error) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <div className="h-[600px]">
        <UsersDataTable
          data={users}
          roles={roles}
          isLoading={loading}
          selectedUserType={selectedUserType}
          selectedRoleId={selectedRoleId}
          search={search}
          onSearchChange={setSearch}
          onUserTypeChange={setSelectedUserType}
          onRoleChange={setSelectedRoleId}
          onDeleteUser={handleDeleteUser}
          onEditUser={handleEditUser}
          onResendOnboardingEmail={handleResendEmail}
          onRefreshData={fetchUsersData}
          onCreateUser={() => setCreateModalOpen(true)}
        />
      </div>

      <CreateUserModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onUserCreated={handleUserCreated}
        roles={roles}
      />

      <EditUserModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        user={userToEdit}
        onUserUpdated={handleUserUpdated}
        roles={roles}
      />
    </>
  );
};

export default Users;
